import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Search, Phone, Mail, Loader2, Package as PackageIcon, Newspaper } from "lucide-react";
import { getEnquiries, updateEnquiryStatus, adminMe, clearToken, getToken } from "../lib/api";
import { COMPANY } from "../data/site";

const STATUS = ["new", "contacted", "closed"];
const badge = {
  new: "bg-[#4ef72d]/20 text-[#0A221C]",
  contacted: "bg-[#f7ad2d]/20 text-[#8a5a00]",
  closed: "bg-black/10 text-[#4A5D58]",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getEnquiries();
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    adminMe()
      .then(load)
      .catch(() => {
        clearToken();
        navigate("/admin/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await updateEnquiryStatus(id, status);
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const matchStatus = filter === "all" || r.status === filter;
      const hay = `${r.name} ${r.phone} ${r.email || ""} ${r.package || ""} ${r.message || ""}`.toLowerCase();
      return matchStatus && hay.includes(q.toLowerCase());
    });
  }, [rows, q, filter]);

  const counts = useMemo(() => ({
    total: rows.length,
    new: rows.filter((r) => r.status === "new").length,
    contacted: rows.filter((r) => r.status === "contacted").length,
    closed: rows.filter((r) => r.status === "closed").length,
  }), [rows]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Top bar */}
      <header className="bg-[#0A221C] text-white">
        <div className="ll-container py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-lg px-3 py-2"><img src={COMPANY.logo} alt="Life Line" className="h-8 w-auto object-contain" /></span>
            <span className="font-display text-xl font-bold hidden sm:block">Enquiry Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/tours" data-testid="admin-nav-tours" className="flex items-center gap-2 text-sm text-white/80 hover:text-white"><PackageIcon size={16} /> Tour Manager</Link>
            <Link to="/admin/blogs" data-testid="admin-nav-blogs" className="flex items-center gap-2 text-sm text-white/80 hover:text-white"><Newspaper size={16} /> Blog Manager</Link>
            <button data-testid="admin-refresh" onClick={load} className="flex items-center gap-2 text-sm text-white/80 hover:text-white"><RefreshCw size={16} /> Refresh</button>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="ll-container py-10">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[{ k: "total", l: "Total Leads" }, { k: "new", l: "New" }, { k: "contacted", l: "Contacted" }, { k: "closed", l: "Closed" }].map((c) => (
            <div key={c.k} className="ll-card p-6" data-testid={`stat-card-${c.k}`}>
              <p className="font-display text-4xl font-bold text-[#0A221C]">{counts[c.k]}</p>
              <p className="text-[#4A5D58] text-sm mt-1">{c.l}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-6">
          <div className="relative max-w-sm w-full">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4A5D58]" />
            <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, package..." className="w-full bg-white border border-black/10 rounded-full pl-11 pr-4 py-3 focus:outline-none focus:border-[#4ef72d]" />
          </div>
          <div className="flex gap-2">
            {["all", ...STATUS].map((s) => (
              <button key={s} data-testid={`admin-filter-${s}`} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${filter === s ? "bg-[#0A221C] text-white" : "text-[#4A5D58] hover:bg-black/5"}`}>{s}</button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="ll-card overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-[#4ef72d]" size={32} /></div>
          ) : filtered.length === 0 ? (
            <p className="p-16 text-center text-[#4A5D58]">No enquiries found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-enquiries-table">
                <thead className="bg-[#0A221C] text-white text-sm">
                  <tr>
                    <th className="p-4 font-semibold">Traveller</th>
                    <th className="p-4 font-semibold">Contact</th>
                    <th className="p-4 font-semibold">Interest</th>
                    <th className="p-4 font-semibold">Message</th>
                    <th className="p-4 font-semibold">Date</th>
                    <th className="p-4 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/5 text-sm">
                  {filtered.map((r) => (
                    <tr key={r.id} data-testid={`enquiry-row-${r.id}`} className="hover:bg-black/[0.02]">
                      <td className="p-4 font-semibold text-[#0A221C] whitespace-nowrap">{r.name}</td>
                      <td className="p-4 text-[#4A5D58]">
                        <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 hover:text-[#0A221C]"><Phone size={13} /> {r.phone}</a>
                        {r.email && <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-[#0A221C] mt-1"><Mail size={13} /> {r.email}</a>}
                      </td>
                      <td className="p-4 text-[#4A5D58] whitespace-nowrap">{r.package || r.destination || "-"}{r.travel_date ? ` · ${r.travel_date}` : ""}</td>
                      <td className="p-4 text-[#4A5D58] max-w-[220px] truncate" title={r.message}>{r.message || "-"}</td>
                      <td className="p-4 text-[#4A5D58] whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}</td>
                      <td className="p-4">
                        <select data-testid={`status-select-${r.id}`} value={r.status} onChange={(e) => changeStatus(r.id, e.target.value)} className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize border-none focus:outline-none cursor-pointer ${badge[r.status] || badge.new}`}>
                          {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
